import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { Component, type ReactNode, Suspense, useEffect, useRef, useState } from "react";
import { useLazyLoadQuery, useRelayEnvironment } from "react-relay";
import { useLocation } from "react-router";
import { fetchQuery, graphql } from "relay-runtime";
import type {
  InternFooterQuery as InternFooterQueryType,
  InternFooterQuery$data,
} from "src/components/InternFooter/__generated__/InternFooterQuery.graphql";
import { getInternFooterIcon } from "src/components/InternFooter/icons";
import { resolveInternFooterEntity } from "src/components/InternFooter/resolveInternFooterEntity";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { usePermissions } from "src/context/MeContext";
import { toast } from "src/hooks/use-toast";

export type { InternFooterEntity } from "src/components/InternFooter/resolveInternFooterEntity";
export { resolveInternFooterEntity };

export const internFooterQuery = graphql`
  query InternFooterQuery($id: GlobalID!) {
    node(id: $id) {
      __typename
      ... on GraphQLLlmConversation {
        internFooterItems {
          id
          kind
          iconKey
          label
          tooltip
          url
          value
          menuItems {
            label
            url
            value
          }
        }
      }
      ... on GraphQLJobRun {
        internFooterItems {
          id
          kind
          iconKey
          label
          tooltip
          url
          value
          menuItems {
            label
            url
            value
          }
        }
      }
    }
  }
`;

// Derive the per-item types from the Relay-generated `InternFooterQuery$data`
// rather than redeclaring them locally — the generated file is the source of
// truth and stays in sync with the schema (frontend/CLAUDE.md: prefer generated
// over manual). Both __typename branches yield the same item shape, so we pick
// the conversation branch with `Extract` and index into it.
type InternFooterItem = Extract<
  NonNullable<InternFooterQuery$data["node"]>,
  { __typename: "GraphQLLlmConversation" }
>["internFooterItems"][number];
type InternFooterMenuItem = InternFooterItem["menuItems"][number];

function extractItems(node: InternFooterQueryType["response"]["node"]): ReadonlyArray<InternFooterItem> {
  if (!node) return [];
  if (node.__typename === "GraphQLLlmConversation" || node.__typename === "GraphQLJobRun") {
    return node.internFooterItems;
  }
  return [];
}

// Lightweight error boundary that swallows errors from InternFooterInner.
// The footer is strictly super-admin chrome — a network/auth/malformed-id
// error reaching the page boundary would crash the whole route for admins, so
// we catch here and render nothing. Errors still surface in Sentry via the
// app-level ErrorBoundary's logging hooks when they bubble past us (they
// won't — we return null), so log directly from componentDidCatch.
class InternFooterErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    if (typeof console !== "undefined") {
      // Intentional: keep this footer silent but visible in the console for
      // local debugging. The footer is admin-only dev chrome, so this doesn't
      // pollute prod logs for end users.
      console.warn("[InternFooter] suppressed error:", error);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export function InternFooter() {
  const { isSuperAdmin } = usePermissions();
  const location = useLocation();
  if (!isSuperAdmin) return null;
  const entity = resolveInternFooterEntity(location);
  if (!entity) return null;
  return (
    // Reset the boundary when the entity changes — without `key`, a transient
    // error on /chat/c/123 would lock the boundary in `hasError` for the rest
    // of the SPA session even after navigating to /chat/c/456 (since router
    // reuses the InternFooter instance across same-route navigations).
    <InternFooterErrorBoundary key={entity.globalId}>
      {/* No fallback during query load: the footer is admin-only chrome and a
          skeleton at the viewport edge is more distracting than helpful, plus
          it would render even when the user has collapsed the footer. */}
      <Suspense fallback={null}>
        <InternFooterInner globalId={entity.globalId} />
      </Suspense>
    </InternFooterErrorBoundary>
  );
}

const HIDDEN_STORAGE_KEY = "internFooter.hidden";

function readHiddenFromStorage(): boolean {
  try {
    return typeof window !== "undefined" && window.localStorage?.getItem(HIDDEN_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeHiddenToStorage(hidden: boolean): void {
  try {
    window.localStorage?.setItem(HIDDEN_STORAGE_KEY, String(hidden));
  } catch {
    // localStorage unavailable (privacy mode, iframe restrictions) — skip.
  }
}

function InternFooterInner({ globalId }: { globalId: string }) {
  const environment = useRelayEnvironment();
  const [fetchKey, setFetchKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isHidden, setIsHidden] = useState<boolean>(readHiddenFromStorage);
  const refreshSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

  const data = useLazyLoadQuery<InternFooterQueryType>(
    internFooterQuery,
    { id: globalId },
    { fetchPolicy: "store-or-network", fetchKey },
  );

  // Cancel any in-flight manual refresh when the component unmounts so we
  // don't leak the observable subscription across navigations.
  useEffect(() => {
    return () => {
      refreshSubscriptionRef.current?.unsubscribe();
      refreshSubscriptionRef.current = null;
    };
  }, []);

  const items = extractItems(data.node);

  const setHidden = (next: boolean) => {
    setIsHidden(next);
    writeHiddenToStorage(next);
  };

  // The hidden short-circuit runs before the empty-items guard so a user who
  // collapses the footer can still see (and click) the peek tab on routes
  // where the backend currently returns no items.
  if (isHidden) {
    return (
      <button
        type="button"
        aria-label="Show intern footer"
        title="Show intern footer"
        onClick={() => setHidden(false)}
        className="fixed bottom-4 right-0 z-40 inline-flex translate-x-[calc(100%-16px)] items-center rounded-l border border-r-0 border-gray-300/60 bg-white/90 py-1 pl-0.5 pr-0.5 text-gray-900 shadow-md backdrop-blur-sm transition-transform duration-200 hover:translate-x-0 dark:border-gray-700/60 dark:bg-gray-900/85 dark:text-gray-100"
      >
        <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    );
  }

  if (items.length === 0) return null;

  const handleRefresh = () => {
    // If an earlier refresh is still pending, cancel it before starting a new one.
    refreshSubscriptionRef.current?.unsubscribe();
    setIsRefreshing(true);
    // Explicitly drive the network request ourselves so we can observe its
    // lifecycle; then bump `fetchKey` so `useLazyLoadQuery` re-reads the store.
    refreshSubscriptionRef.current = fetchQuery(environment, internFooterQuery, { id: globalId }).subscribe({
      complete: () => {
        setFetchKey((prev) => prev + 1);
        setIsRefreshing(false);
        refreshSubscriptionRef.current = null;
      },
      error: () => {
        setIsRefreshing(false);
        refreshSubscriptionRef.current = null;
      },
    });
  };

  const openLink = (url: string) => window.open(url, "_blank", "noopener,noreferrer");

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ variant: "success", title: `Copied ${label}` });
    } catch {
      toast({ variant: "error", title: "Clipboard unavailable" });
    }
  };

  // Use `!= null` (covers both null and undefined) when checking value/url so a
  // misconfigured backend row that emits `undefined` doesn't slip past `!== null`
  // and cause a TypeError downstream. When neither url nor value is present,
  // log a console.warn so the misconfiguration is diagnosable in dev tools.
  const handleClick = async (item: InternFooterItem) => {
    if (item.kind === "LINK" && item.url != null) {
      openLink(item.url);
      return;
    }
    if (item.kind === "COPY" && item.value != null) {
      await copyToClipboard(item.value, item.label);
      return;
    }
    console.warn("[InternFooter] item has neither url nor value:", { id: item.id, kind: item.kind, label: item.label });
  };

  const handleMenuItemSelect = async (parentLabel: string, sub: InternFooterMenuItem) => {
    if (sub.url != null) {
      openLink(sub.url);
      return;
    }
    if (sub.value != null) {
      await copyToClipboard(sub.value, `${parentLabel}: ${sub.label}`);
      return;
    }
    console.warn("[InternFooter] menu item has neither url nor value:", { parentLabel, label: sub.label });
  };

  return (
    <footer
      role="region"
      aria-label="Intern footer"
      className="fixed bottom-0 left-1/2 z-40 w-fit -translate-x-1/2 rounded-t-lg border border-b-0 border-gray-300/60 bg-white/90 px-2 py-1 text-xs text-gray-900 shadow-md backdrop-blur-sm dark:border-gray-700/60 dark:bg-gray-900/85 dark:text-gray-100"
    >
      <div className="flex items-center gap-1">
        <span
          aria-hidden="true"
          className="select-none rounded bg-gray-200/70 px-1 py-px text-[9px] font-semibold uppercase tracking-wider text-gray-600 dark:bg-gray-700/70 dark:text-gray-300"
        >
          Intern
        </span>
        {items.map((item) => {
          const Icon = getInternFooterIcon(item.iconKey);
          if (item.kind === "MENU") {
            return (
              <DropdownMenu key={item.id}>
                <DropdownMenuTrigger
                  title={item.tooltip}
                  aria-label={item.label}
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-black/5 focus:outline-none data-[state=open]:bg-black/10 dark:hover:bg-white/10 dark:data-[state=open]:bg-white/15"
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>{item.label}</span>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" side="top" className="min-w-40">
                  {item.menuItems.map((sub, idx) => (
                    <DropdownMenuItem
                      key={`${item.id}-${idx}`}
                      onSelect={() => handleMenuItemSelect(item.label, sub)}
                    >
                      {sub.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          }
          return (
            <button
              key={item.id}
              type="button"
              title={item.tooltip}
              aria-label={item.label}
              onClick={() => handleClick(item)}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-black/5 dark:hover:bg-white/10"
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{item.label}</span>
            </button>
          );
        })}
        <button
          type="button"
          aria-label="Refresh"
          title="Refresh"
          disabled={isRefreshing}
          onClick={handleRefresh}
          className="inline-flex items-center rounded px-1 py-0.5 hover:bg-black/5 disabled:opacity-40 dark:hover:bg-white/10"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? "animate-spin" : ""}`} aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Hide"
          title="Hide (slides to right edge)"
          onClick={() => setHidden(true)}
          className="inline-flex items-center rounded px-1 py-0.5 hover:bg-black/5 dark:hover:bg-white/10"
        >
          <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    </footer>
  );
}
